/* The change log - ADM-06.
 *
 * "Every change to settings, master data, calendars, inputs and plans is
 * recorded with user, date, old value and new value."
 *
 * The requirement is about the RECORD; this screen is about making it
 * readable. A planner checking who changed a Saturday should see a sentence,
 * not `{"applied_cells": 154}` - raw JSON is what the database stores, not
 * what a person reads. So each action gets a plain-language rendering, and
 * the raw payload stays available behind a toggle for the one case where it
 * matters.
 */

import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'

interface Entry {
  at: string
  actor: string
  action: string
  target: string
  old: unknown
  new: unknown
}

/* How each action reads in plain language. The action key is the stable
 * contract; the sentence is presentation. */
const ACTIONS: Record<string, { label: string; icon: string; tone: string }> = {
  'plan.days': { label: 'Working days changed', icon: '✎', tone: 'neutral' },
  'plan.auto': { label: 'Agentic plan applied', icon: '✦', tone: 'good' },
  'simulation.choose': { label: 'Simulation applied', icon: '●', tone: 'good' },
  'cycle.state': { label: 'Cycle moved', icon: '→', tone: 'neutral' },
  'cycle.seeded': { label: 'Cycle opened', icon: '＋', tone: 'good' },
  'scenario.create': { label: 'Scenario created', icon: '⎘', tone: 'neutral' },
  'scenario.archive': { label: 'Scenario archived', icon: '⌫', tone: 'neutral' },
  'scenario.demand': { label: 'Demand overridden', icon: '±', tone: 'warning' },
  'review.submit': { label: 'Submitted for approval', icon: '↑', tone: 'neutral' },
  'review.return': { label: 'Returned with a comment', icon: '↩', tone: 'warning' },
  'review.publish': { label: 'Plan published', icon: '✓', tone: 'good' },
  'review.reopen': { label: 'Published plan reopened', icon: '↺', tone: 'warning' },
  'artifact.create': { label: 'Report produced', icon: '⤓', tone: 'neutral' },
}

export function AuditLog() {
  const [data, setData] = useState<{ count: number; entries: Entry[] } | null>(null)
  const [filter, setFilter] = useState('')
  const [raw, setRaw] = useState(false)

  useEffect(() => {
    void api.audit().then(setData)
  }, [])

  const filtered = useMemo(() => {
    if (!data) return []
    if (!filter) return data.entries
    return data.entries.filter((e) => e.action.startsWith(filter))
  }, [data, filter])

  const families = useMemo(() => {
    if (!data) return []
    return [...new Set(data.entries.map((e) => e.action.split('.')[0]))].sort()
  }, [data])

  if (!data) return <div className="card" style={{ padding: 20 }}>Loading…</div>

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div className="card" style={{ padding: '14px 18px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div>
          <h1>Change log</h1>
          <div className="small muted">
            {data.count} changes, each attributed to an account with its old and new value
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="small">
            <option value="">All actions</option>
            {families.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <label className="small muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={raw} onChange={(e) => setRaw(e.target.checked)} />
            show raw values
          </label>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ maxHeight: '72vh', overflow: 'auto' }}>
          {filtered.map((e, i) => {
            const meta = ACTIONS[e.action] ?? { label: e.action, icon: '·', tone: 'neutral' }
            return (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '150px 26px 1fr 170px',
                  gap: 12,
                  alignItems: 'baseline',
                  padding: '11px 18px',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span className="mono small muted" style={{ whiteSpace: 'nowrap' }}>
                  {formatWhen(e.at)}
                </span>
                <span
                  aria-hidden="true"
                  title={meta.label}
                  style={{
                    color:
                      meta.tone === 'good'
                        ? 'var(--status-good)'
                        : meta.tone === 'warning'
                          ? 'var(--status-warning)'
                          : 'var(--text-muted)',
                    fontSize: 15,
                  }}
                >
                  {meta.icon}
                </span>
                <div>
                  <div style={{ fontWeight: 500 }}>{describe(e, meta.label)}</div>
                  {raw && (
                    <div className="mono small muted" style={{ marginTop: 3 }}>
                      {e.action} · {String(e.old ?? '—')} → {String(e.new ?? '—')}
                    </div>
                  )}
                </div>
                <span className="small muted" style={{ textAlign: 'right' }}>
                  {e.actor}
                </span>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div style={{ padding: 28, textAlign: 'center' }} className="muted small">
              No changes recorded for this filter.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** One sentence per action, built from its own old/new values. */
function describe(e: Entry, fallback: string): string {
  const oldValue = decode(e.old)
  const newValue = decode(e.new)

  switch (e.action) {
    case 'plan.days': {
      // target is "family/crew/bucket"
      const [family, crew, bucket] = e.target.split('/')
      return `${family} · ${crew} · ${bucket} — working days ${oldValue} → ${newValue}`
    }
    case 'plan.auto': {
      const cells = pick(e.new, 'applied_cells')
      return `${e.target} — ${cells ?? 'some'} cells applied from the agentic plan`
    }
    case 'cycle.state':
      return `Cycle moved from ${oldValue} to ${newValue}`
    case 'cycle.seeded': {
      const streams = pick(e.new, 'value_streams')
      const source = pick(e.new, 'source')
      return `${e.target} opened with ${streams ?? '?'} value streams${source ? ` from ${source}` : ''}`
    }
    case 'scenario.create':
      return `Scenario “${newValue}” created as a copy of “${oldValue}”`
    case 'scenario.demand': {
      const factor = pick(e.new, 'factor')
      const from = pick(e.new, 'from')
      const percent = factor ? `${Math.round((Number(factor) - 1) * 100)}%` : '?'
      return `Demand changed by ${percent} on “${e.target}”, from bucket ${from ?? 0}`
    }
    case 'review.submit':
      return `“${e.target}” submitted for approval${newValue !== '—' ? ` — “${newValue}”` : ''}`
    case 'review.return':
      return `“${e.target}” returned${newValue !== '—' ? ` — “${newValue}”` : ''}`
    case 'review.publish':
      return `“${e.target}” published as the plan of record`
    case 'review.reopen':
      return `Published plan reopened; the earlier version is kept as “${newValue}”`
    case 'artifact.create': {
      const bytes = pick(e.new, 'bytes')
      return `${e.target} generated${bytes ? ` (${Math.round(Number(bytes) / 1024)} kB)` : ''}`
    }
    case 'scenario.archive':
      return `Scenario “${e.target}” archived`
    default:
      return `${fallback} — ${e.target}`
  }
}

/** The API stores values JSON-encoded; render the value, not its encoding. */
function decode(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return typeof parsed === 'object' ? JSON.stringify(parsed) : String(parsed)
    } catch {
      return value
    }
  }
  return String(value)
}

function pick(value: unknown, key: string): string | null {
  if (typeof value !== 'string') return null
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' && key in parsed ? String(parsed[key]) : null
  } catch {
    return null
  }
}

function formatWhen(iso: string): string {
  if (!iso) return '—'
  return iso.replace('T', '  ').slice(0, 16)
}
